import { fetchData } from './api';
import { selectCurrentGroupId } from './groups_slice';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../store';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface AsyncDataSlot {
  data: unknown;
  status: AsyncStatus;
  error: string | null;
}

interface PatientSatisfactionState {
  hcahps_data: AsyncDataSlot;
  rollingHighBurnout: AsyncDataSlot;
}

const initialState: PatientSatisfactionState = {
  hcahps_data: { data: null, status: 'idle', error: null },
  rollingHighBurnout: { data: null, status: 'idle', error: null },
};

export const fetchPatientSatisfaction = createAsyncThunk<unknown, void, { state: RootState }>(
  'patientSatisfaction/fetch',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const isSeAdmin = state.authentication.user?.burnout_role === 'se_admin';
      const practice_group_id = isSeAdmin
        ? selectCurrentGroupId(state)
        : (state.authentication.user?.practice_group_ids as number[])?.[0];

      if (!practice_group_id) return rejectWithValue('No practice_group_id');

      return await fetchData({ endpoint: 'cms_hcahps_results/summary', filters: { practice_group_id } });
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch patient satisfaction');
    }
  },
  {
    condition: (_, { getState }) => getState().patient_satisfaction.hcahps_data.status !== 'loading',
  }
);

export const fetchRollingHighBurnout = createAsyncThunk<unknown, any, { state: RootState }>(
  'patientSatisfaction/fetchRollingHighBurnout',
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const isSeAdmin = state.authentication.user?.burnout_role === 'se_admin';
      const practice_group_id =
        params.practice_group_id ??
        (isSeAdmin ? selectCurrentGroupId(state) : (state.authentication.user?.practice_group_ids as number[])?.[0]);

      if (!practice_group_id) return rejectWithValue('No practice_group_id');

      const start_date = params.start_date ?? '2025-06-01';
      return await fetchData({
        endpoint: 'insights/patient_satisfaction/rolling_high_burnout',
        filters: { practice_group_id, start_date },
      });
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch rolling high burnout');
    }
  },
  {
    condition: (_, { getState }) =>
      getState().patient_satisfaction.rollingHighBurnout.status !== 'loading',
  }
);

const patientSatisfactionSlice = createSlice({
  name: 'patientSatisfaction',
  initialState,
  reducers: {
    clearPatientSatisfactionError(state) {
      state.hcahps_data.error = null;
    },
    resetPatientSatisfaction(state) {
      state.hcahps_data = { data: null, status: 'idle', error: null };
    },
    clearRollingHighBurnoutError(state) {
      state.rollingHighBurnout.error = null;
    },
    resetRollingHighBurnout(state) {
      state.rollingHighBurnout = { data: null, status: 'idle', error: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPatientSatisfaction.pending, (state) => {
        state.hcahps_data.status = 'loading';
        state.hcahps_data.error = null;
      })
      .addCase(fetchPatientSatisfaction.fulfilled, (state, action) => {
        state.hcahps_data.status = 'succeeded';
        state.hcahps_data.data = action.payload;
      })
      .addCase(fetchPatientSatisfaction.rejected, (state, action) => {
        state.hcahps_data.status = 'failed';
        state.hcahps_data.error = (action.payload as string) ?? action.error?.message ?? null;
      })
      .addCase(fetchRollingHighBurnout.pending, (state) => {
        state.rollingHighBurnout.status = 'loading';
        state.rollingHighBurnout.error = null;
      })
      .addCase(fetchRollingHighBurnout.fulfilled, (state, action) => {
        state.rollingHighBurnout.status = 'succeeded';
        state.rollingHighBurnout.data = action.payload;
      })
      .addCase(fetchRollingHighBurnout.rejected, (state, action) => {
        state.rollingHighBurnout.status = 'failed';
        state.rollingHighBurnout.error = (action.payload as string) ?? action.error?.message ?? null;
      });
  },
});

export const { clearRollingHighBurnoutError, resetRollingHighBurnout } = patientSatisfactionSlice.actions;
export default patientSatisfactionSlice.reducer;
